import { useContext } from "react";
import { NavLink } from "react-router-dom";
import {
  LayoutDashboard, Mail, Users, FileText,
  PieChart, BarChart2, Settings, Workflow,
  ChevronLeft, ChevronRight, Zap,
} from "lucide-react";
import { AuthContext } from "../context/AuthContext";
import Avatar from "../modules/components/ui/Avatar";
import { useState } from "react";

const NAV_GROUPS = [
  {
    label: "Main",
    items: [
      { label: "Dashboard",  path: "/swiftreach/dashboard",  icon: LayoutDashboard },
      { label: "Campaigns",  path: "/swiftreach/campaigns",  icon: Mail },
      { label: "Automations",path: "/swiftreach/automations",icon: Workflow },
      { label: "Contacts",   path: "/swiftreach/contacts",   icon: Users },
      { label: "Templates",  path: "/swiftreach/templates",  icon: FileText },
    ],
  },
  {
    label: "Insights",
    items: [
      { label: "Segments",   path: "/swiftreach/segments",   icon: PieChart },
      { label: "Analytics",  path: "/swiftreach/analytics",  icon: BarChart2 },
    ],
  },
  {
    label: "System",
    items: [
      { label: "Settings",   path: "/swiftreach/settings",   icon: Settings },
    ],
  },
];

function NavItem({ label, path, icon: Icon, collapsed }) {
  return (
    <NavLink
      to={path}
      title={collapsed ? label : undefined}
      className="block"
    >
      {({ isActive }) => (
        <div
          className={[
            "relative flex items-center rounded-xl transition-all duration-200 my-0.5",
            collapsed ? "justify-center p-[10px] mx-auto w-12" : "px-3 py-2 mx-2",
            isActive ? "text-white" : "text-white/50 hover:text-white/85 hover:bg-white/[0.06]",
          ].join(" ")}
          style={
            isActive
              ? {
                  background: "rgba(255,255,255,0.11)",
                  boxShadow: "0 2px 12px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.1)",
                }
              : {}
          }
        >
          {/* Active left accent bar */}
          {isActive && !collapsed && (
            <span
              className="absolute left-0 top-1/2 -translate-y-1/2 rounded-r-full"
              style={{
                width: "3px",
                height: "58%",
                background: "var(--color-secondary)",
                boxShadow: "0 0 10px var(--color-secondary)",
              }}
            />
          )}

          {/* Active dot for collapsed mode */}
          {isActive && collapsed && (
            <span
              className="absolute top-1.5 right-1.5 rounded-full"
              style={{
                width: "5px",
                height: "5px",
                background: "var(--color-secondary)",
                boxShadow: "0 0 6px var(--color-secondary)",
              }}
            />
          )}

          {/* Icon chip */}
          <div
            className="flex items-center justify-center rounded-lg flex-shrink-0 transition-all duration-200"
            style={{
              width: "30px",
              height: "30px",
              background: isActive
                ? "rgba(255,255,255,0.16)"
                : "rgba(255,255,255,0.04)",
            }}
          >
            <Icon className="w-[15px] h-[15px]" />
          </div>

          {/* Label */}
          {!collapsed && (
            <span
              className="ml-3 text-[13px] font-medium truncate leading-none"
              style={{ letterSpacing: "-0.15px" }}
            >
              {label}
            </span>
          )}
        </div>
      )}
    </NavLink>
  );
}

export default function Sidebar() {
  const { user } = useContext(AuthContext);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("sr-sidebar-collapsed") === "true"; } catch { return false; }
  });

  const toggle = () => {
    setCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem("sr-sidebar-collapsed", String(next)); } catch {}
      return next;
    });
  };

  return (
    <aside
      className="relative flex flex-col h-full flex-shrink-0 overflow-hidden"
      style={{
        width: collapsed ? "72px" : "240px",
        transition: "width 280ms cubic-bezier(0.4, 0, 0.2, 1)",
        background: "linear-gradient(175deg, var(--color-primary) 0%, var(--color-primary-hover) 100%)",
      }}
    >
      {/* Decorative ambient orb */}
      <div
        className="absolute pointer-events-none"
        style={{
          top: "-60px",
          right: "-40px",
          width: "180px",
          height: "180px",
          background: "radial-gradient(circle, rgba(255,255,255,0.06) 0%, transparent 70%)",
        }}
      />

      {/* ── Brand Header ── */}
      <div
        className="flex items-center flex-shrink-0 px-3 py-4"
        style={{ borderBottom: "1px solid rgba(255,255,255,0.07)" }}
      >
        {/* Logo icon */}
        <div
          className="flex items-center justify-center rounded-xl flex-shrink-0"
          style={{
            width: "36px",
            height: "36px",
            background: "linear-gradient(135deg, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0.08) 100%)",
            border: "1px solid rgba(255,255,255,0.18)",
            boxShadow: "0 4px 14px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.2)",
            marginLeft: collapsed ? "auto" : undefined,
            marginRight: collapsed ? "auto" : undefined,
          }}
        >
          <Zap className="w-[18px] h-[18px] text-white" />
        </div>

        {/* Brand text */}
        {!collapsed && (
          <div className="ml-3 overflow-hidden">
            <div className="flex items-center gap-2">
              <p
                className="font-bold text-white leading-none"
                style={{ fontSize: "13.5px", letterSpacing: "-0.4px" }}
              >
                SwiftReach
              </p>
              <span
                className="rounded-full font-bold leading-none"
                style={{
                  fontSize: "9px",
                  padding: "2px 6px",
                  background: "rgba(255,255,255,0.14)",
                  color: "rgba(255,255,255,0.75)",
                  letterSpacing: "0.8px",
                  border: "1px solid rgba(255,255,255,0.1)",
                }}
              >
                PRO
              </span>
            </div>
            <p
              className="mt-0.5 leading-none truncate"
              style={{ fontSize: "10.5px", color: "rgba(255,255,255,0.38)", letterSpacing: "0.1px" }}
            >
              Mail Marketing Suite
            </p>
          </div>
        )}
      </div>

      {/* ── Navigation ── */}
      <nav className="flex-1 overflow-y-auto overflow-x-hidden pt-2 pb-1">
        {NAV_GROUPS.map((group, gi) => (
          <div key={gi} style={{ marginTop: gi > 0 ? "10px" : "0" }}>
            {/* Section label */}
            {!collapsed && (
              <p
                className="px-5 mb-1 font-semibold uppercase tracking-widest truncate"
                style={{
                  fontSize: "9px",
                  color: "rgba(255,255,255,0.25)",
                  marginTop: gi > 0 ? "4px" : "2px",
                }}
              >
                {group.label}
              </p>
            )}
            {collapsed && gi > 0 && (
              <div
                className="mx-auto my-2"
                style={{
                  width: "28px",
                  height: "1px",
                  background: "rgba(255,255,255,0.08)",
                }}
              />
            )}

            {group.items.map(item => (
              <NavItem key={item.path} {...item} collapsed={collapsed} />
            ))}
          </div>
        ))}
      </nav>

      {/* ── Bottom: User + Collapse Toggle ── */}
      <div
        className="flex-shrink-0 p-2"
        style={{ borderTop: "1px solid rgba(255,255,255,0.07)" }}
      >
        {/* User profile card */}
        <div
          className={[
            "flex items-center rounded-xl px-2 py-2 mb-1.5 transition-colors duration-200",
            collapsed ? "justify-center" : "gap-2.5",
          ].join(" ")}
          style={{ background: "rgba(255,255,255,0.05)" }}
        >
          <Avatar user={user} size={28} />

          {!collapsed && user && (
            <div className="overflow-hidden flex-1">
              <p
                className="font-semibold text-white truncate leading-tight"
                style={{ fontSize: "11.5px", letterSpacing: "-0.1px" }}
              >
                {user.name ?? user.email?.split("@")[0]}
              </p>
              <p
                className="truncate leading-tight"
                style={{ fontSize: "10px", color: "rgba(255,255,255,0.35)" }}
              >
                {user.email}
              </p>
            </div>
          )}
        </div>

        {/* Collapse toggle */}
        <button
          onClick={toggle}
          className={[
            "w-full flex items-center rounded-xl px-2 py-2 transition-all duration-200",
            collapsed ? "justify-center" : "justify-between",
          ].join(" ")}
          style={{ color: "rgba(255,255,255,0.35)" }}
          onMouseEnter={e => {
            e.currentTarget.style.background = "rgba(255,255,255,0.07)";
            e.currentTarget.style.color = "rgba(255,255,255,0.7)";
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.color = "rgba(255,255,255,0.35)";
          }}
        >
          {!collapsed && (
            <span style={{ fontSize: "11px", letterSpacing: "0.1px" }}>
              Collapse sidebar
            </span>
          )}
          {collapsed
            ? <ChevronRight className="w-3.5 h-3.5" />
            : <ChevronLeft className="w-3.5 h-3.5" />
          }
        </button>
      </div>
    </aside>
  );
}
